import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { IHeaderParams } from '@ag-grid-community/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { Component } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { IconComponent } from '@shared/components/icon/icon.component';
import { AgSetColumnsVisible } from '@shared/utils';

interface CollapsibleHeaderParams extends IHeaderParams {
  collapsedByDefault: boolean;
  collapsibleColumns: string[];
  isColumnCollapsed$?: BehaviorSubject<boolean>;
  shouldAutoSize?: boolean;
}

@Component({
  host: {
    class: 'w-full block',
  },
  imports: [IconComponent, AsyncPipe, NgClass],
  template: `
    <div class="flex w-full items-center justify-center space-x-2">
      <span
        class="truncate text-base text-aux-gray-darkest"
        [ngClass]="{ 'cursor-pointer': isSortable }"
        (click)="onSortClick()"
      >
        {{ params.displayName }}
      </span>
      <button class="aux-link flex items-center no-underline" (click)="onChangeClick()">
        @if (params.column.isSortAscending()) {
          <span ref="eSortAsc" class="ag-sort-indicator-icon ag-sort-ascending-icon">
            <span class="ag-icon ag-icon-asc" unselectable="on" role="presentation"></span>
          </span>
        }

        @if (params.column.isSortDescending()) {
          <span ref="eSortDesc" class="ag-sort-indicator-icon ag-sort-descending-icon">
            <span class="ag-icon ag-icon-desc" unselectable="on" role="presentation"></span>
          </span>
        }

        <aux-icon
          class="text-aux-gray-darkest"
          [name]="(isExpanded$ | async) ? 'ChevronLeft' : 'ChevronRight'"
        />
      </button>
    </div>
  `,
})
export class AgCollapsibleHeaderComponent implements IHeaderAngularComp {
  isExpanded$ = new BehaviorSubject(true);
  isSortable = false;
  params!: CollapsibleHeaderParams;

  agInit(params: CollapsibleHeaderParams): void {
    this.params = params;

    this.isExpanded$.next(!this.params.collapsedByDefault);
    this.params.isColumnCollapsed$?.next(!this.isExpanded$.getValue());

    this.isSortable = !!params.column.getUserProvidedColDef()?.sortable;
  }

  onChangeClick() {
    this.isExpanded$.next(!this.isExpanded$.getValue());
    this.params.isColumnCollapsed$?.next(!this.isExpanded$.getValue());
    this.updateVisibilityColumns();
  }

  onSortClick() {
    if (!this.isSortable) return;

    const sort = this.params.column.getSort();
    const sortingAscending = sort === 'asc';
    const sortingDescending = sort === 'desc';
    const notSorting = !sortingAscending && !sortingDescending;

    if (notSorting) {
      this.params.setSort('asc');

      return;
    }

    if (sortingAscending) {
      this.params.setSort('desc');

      return;
    }

    if (sortingDescending) {
      this.params.setSort(null);

      return;
    }
  }

  refresh(): boolean {
    return false;
  }

  updateVisibilityColumns() {
    if (!this.params.collapsibleColumns?.length) {
      console.warn('No collapsible columns has been found');
    }

    AgSetColumnsVisible({
      gridApi: this.params.api,
      keys: this.params.collapsibleColumns ?? [],
      visible: this.isExpanded$.getValue(),
    });

    if (this.params.shouldAutoSize ?? true) {
      this.params.api.sizeColumnsToFit();
    }
  }
}
